import { Link } from 'react-router';
import {
  estimateInterest,
  formatPeso,
  type AccountStatus,
  type CommitmentItem,
  type ComingInItem,
} from '@tahi/core';
import { cn } from '../../../ui/cn';
import { fitFontSize } from '../../../ui/fitText';
import { spokenPeso } from '../../../ui/spokenPeso';
import { copy } from '../copy';
import {
  accountStatusesOf,
  moneyOnHandOf,
  owedToYouOf,
  receivablesOf,
  safeToSpendOf,
  savingsOf,
  spendingStatusesOf,
  type BudgetSnapshot,
  type SafeToSpendView,
} from '../derive';
import { dateWithYear, dayShort } from '../format';
import { CARD, FOCUS, LABEL, QUIET_LINK } from '../styles';

/** "₱1,234" or "−₱1,234": the minus sign is a real minus, never hidden in a small word. */
export function signedPeso(minor: number, cents: 'auto' | 'always' = 'auto'): string {
  const text = formatPeso(Math.abs(minor), { cents });
  return minor < 0 ? `−${text}` : text;
}

/** A one-line amount that always fits its card (DM Mono, scales down with the container). */
export function BigAmount({
  minor,
  maxPx,
  className,
  testId,
}: {
  minor: number;
  maxPx: number;
  className?: string;
  testId?: string;
}) {
  const text = signedPeso(minor);
  return (
    <span
      data-testid={testId}
      className={cn(
        'block whitespace-nowrap font-mono tabular-nums leading-tight',
        minor < 0 ? 'text-destructive' : 'text-fg',
        className,
      )}
      style={{ fontSize: fitFontSize(text, maxPx) }}
    >
      {text}
    </span>
  );
}

const shortDay = (d: string) => dayShort(d).replace(/^\w+, /, '');

/** The plain line under Safe to spend. */
export function safeLine(v: SafeToSpendView, s: BudgetSnapshot): string {
  if (v.basis === 'cash') {
    return (
      copy.onHandAndDue(v.onHandMinor, v.reservedMinor) +
      (v.expectedMinor > 0 ? copy.plusExpected(v.expectedMinor) : '')
    );
  }
  if (v.basis === 'fund') return copy.limitLeft(v.amountMinor + v.reservedMinor, v.reservedMinor);
  void s;
  return copy.afterPayments(v.reservedMinor);
}

/** The hero (Ivan, 2026-11-03): one number, the day it has to last until, one plain line. */
export function SafeToSpendHero({ s }: { s: BudgetSnapshot }) {
  const v = safeToSpendOf(s);
  const day = shortDay(v.nextStart);
  const short = v.amountMinor < 0;
  return (
    <section
      aria-label={`${copy.safeUntil(day)}, ${short ? 'short ' : ''}${spokenPeso(Math.abs(v.amountMinor))}`}
      className={`${CARD} flex flex-col gap-1 [container-type:inline-size]`}
    >
      <h2 className={LABEL}>{copy.safeUntil(day)}</h2>
      <BigAmount minor={v.amountMinor} maxPx={44} testId="safe-to-spend" />
      {short ? (
        <p className="text-sm text-destructive">
          {copy.shortUntil(day)} —{' '}
          <a href="#due-before-payday" className={`underline ${FOCUS}`}>
            {copy.seeWhatsDue}
          </a>
        </p>
      ) : null}
      <p className="text-sm text-muted">{safeLine(v, s)}</p>
      {v.basis === 'cashflow' ? (
        <p className="text-sm text-muted">
          {copy.addYourAccounts}{' '}
          <Link to="/m/budget/accounts/new" className={`text-primary ${FOCUS}`}>
            {copy.addAccount}
          </Link>
        </p>
      ) : null}
    </section>
  );
}

/** "Your money": each account with its balance, and the total. */
export function YourMoneyCard({ s, compact = false }: { s: BudgetSnapshot; compact?: boolean }) {
  const statuses = spendingStatusesOf(s);
  const total = moneyOnHandOf(s);
  const anyAccount = accountStatusesOf(s).length > 0;
  return (
    <section aria-label={copy.yourMoney} className={`${CARD} flex flex-col gap-2`}>
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-[20px] font-bold text-fg">{copy.yourMoney}</h2>
        <Link to="/m/budget/accounts" className={QUIET_LINK}>
          {copy.accounts}
        </Link>
      </div>
      {statuses.length === 0 && anyAccount ? (
        <p className="text-sm text-muted">{copy.noSpendingAccounts}</p>
      ) : statuses.length === 0 ? (
        <p className="text-sm text-muted">
          {copy.addYourAccounts}{' '}
          <Link to="/m/budget/accounts/new" className={`text-primary ${FOCUS}`}>
            {copy.addAccount}
          </Link>
        </p>
      ) : (
        <ul aria-label={copy.accounts} className="divide-y divide-divider">
          {(compact ? statuses.slice(0, 5) : statuses).map((a) => (
            <AccountRow key={a.account.id} a={a} />
          ))}
          <li
            aria-label={`${copy.total}, ${spokenPeso(Math.abs(total))}`}
            className="flex min-h-11 items-center justify-between gap-3 font-bold"
          >
            <span className="text-fg">{copy.total}</span>
            <span
              data-testid="money-on-hand"
              className={cn('font-mono tabular-nums', total < 0 ? 'text-destructive' : 'text-fg')}
            >
              {signedPeso(total)}
            </span>
          </li>
        </ul>
      )}
    </section>
  );
}

function AccountRow({ a, detail }: { a: AccountStatus; detail?: string | null }) {
  return (
    <li>
      <Link
        to={`/m/budget/accounts/${a.account.id}`}
        aria-label={`${a.account.name}, ${a.balanceMinor < 0 ? 'minus ' : ''}${spokenPeso(Math.abs(a.balanceMinor))}`}
        className={`flex min-h-11 items-center justify-between gap-3 py-1 ${FOCUS}`}
      >
        <span className="min-w-0 text-fg">
          <span className="block truncate">{a.account.name}</span>
          {detail ? <span className="block text-sm text-muted">{detail}</span> : null}
        </span>
        <span
          className={cn(
            'shrink-0 font-mono tabular-nums',
            a.balanceMinor < 0 ? 'text-destructive' : 'text-fg',
          )}
        >
          {signedPeso(a.balanceMinor)}
        </span>
      </Link>
    </li>
  );
}

/** "3% a year · Matures Nov 1, 2031" under a savings account's name. */
export function savingsDetail(a: AccountStatus['account']): string | null {
  const parts: string[] = [];
  if (a.annualRateBp) parts.push(copy.ratePerYear(String(a.annualRateBp / 100)));
  if (a.maturityDate)
    parts.push(
      a.locked
        ? copy.lockedUntil(dateWithYear(a.maturityDate))
        : copy.maturesOn(dateWithYear(a.maturityDate)),
    );
  return parts.length > 0 ? parts.join(' · ') : null;
}

/**
 * v0.6 "Savings": savings-only accounts with their total and "Interest (estimated)". Never in
 * Safe to spend. Null when there are none.
 */
export function SavingsCard({ s }: { s: BudgetSnapshot }) {
  const sum = savingsOf(s);
  if (sum.accounts.length === 0) return null;
  return (
    <section aria-label={copy.savings} className={`${CARD} flex flex-col gap-2`}>
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-[20px] font-bold text-fg">{copy.savings}</h2>
        <Link to="/m/budget/accounts" className={QUIET_LINK}>
          {copy.accounts}
        </Link>
      </div>
      <ul aria-label={copy.savings} className="divide-y divide-divider">
        {sum.accounts.map((a) => (
          <AccountRow key={a.account.id} a={a} detail={savingsDetail(a.account)} />
        ))}
        <li
          aria-label={`${copy.total}, ${spokenPeso(Math.abs(sum.totalMinor))}`}
          className="flex min-h-11 items-center justify-between gap-3 font-bold"
        >
          <span className="text-fg">{copy.total}</span>
          <span data-testid="savings-total" className="font-mono tabular-nums text-fg">
            {signedPeso(sum.totalMinor)}
          </span>
        </li>
        {sum.interestMinor > 0 ? (
          <li className="flex min-h-11 items-center justify-between gap-3">
            <span className="text-muted">{copy.interestEstimated}</span>
            <span data-testid="savings-interest" className="font-mono tabular-nums text-muted">
              +{formatPeso(sum.interestMinor, { cents: 'auto' })}
            </span>
          </li>
        ) : null}
      </ul>
      <p className="text-sm text-muted">{copy.savingsNote}</p>
    </section>
  );
}

/** Interest numbers for one account, or null without a rate (used on the account screen). */
export function interestOf(s: BudgetSnapshot, accountId: string) {
  const a = s.accounts.find((x) => x.id === accountId);
  return a ? estimateInterest(a, s.accountEntries, s.today) : null;
}

/** v0.6 "Owed to you": who owes what's left, and the total. Never in Safe to spend. */
export function OwedToYouCard({ s, max = 4 }: { s: BudgetSnapshot; max?: number }) {
  const open = receivablesOf(s).filter((r) => !r.settled);
  const total = owedToYouOf(s);
  return (
    <section aria-label={copy.owedToYou} className={`${CARD} flex flex-col gap-2`}>
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-[20px] font-bold text-fg">{copy.owedToYou}</h2>
        <Link to="/m/budget/owed" className={QUIET_LINK}>
          {copy.seeOwed}
        </Link>
      </div>
      {open.length === 0 ? (
        <p className="text-sm text-muted">{copy.nothingOwed}</p>
      ) : (
        <>
          <ul aria-label={copy.owedToYou} className="divide-y divide-divider">
            {open.slice(0, max).map((r) => (
              <li key={r.receivable.id}>
                <Link
                  to={`/m/budget/owed/${r.receivable.id}/receive`}
                  className={`flex min-h-11 items-center justify-between gap-3 py-1 ${FOCUS}`}
                >
                  <span className="min-w-0 text-fg">
                    <span className="block truncate">{r.receivable.name}</span>
                    {r.receivable.expectedDate || r.receivedMinor > 0 ? (
                      <span className="block text-sm text-muted">
                        {r.receivable.expectedDate ? dayShort(r.receivable.expectedDate) : ''}
                        {r.receivable.expectedDate && r.receivedMinor > 0 ? ' · ' : ''}
                        {r.receivedMinor > 0
                          ? copy.owedLeft(r.remainingMinor, r.receivable.amountMinor)
                          : ''}
                      </span>
                    ) : null}
                  </span>
                  <span className="shrink-0 font-mono tabular-nums text-fg">
                    {formatPeso(r.remainingMinor, { cents: 'auto' })}
                  </span>
                </Link>
              </li>
            ))}
            {open.length > max ? (
              <li className="py-2 text-sm text-muted">+{open.length - max} more</li>
            ) : null}
          </ul>
          <p className="text-sm text-muted">
            <span data-testid="owed-total">{copy.owedTotal(total)}</span> · {copy.owedNote}
          </p>
        </>
      )}
    </section>
  );
}

/** "Due before payday": the payments Safe to spend holds back, with dates. */
export function DueBeforePayday({ items, max = 6 }: { items: CommitmentItem[]; max?: number }) {
  return (
    <section
      id="due-before-payday"
      aria-label={copy.dueBeforePayday}
      className={`${CARD} flex flex-col gap-2`}
    >
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-[20px] font-bold text-fg">{copy.dueBeforePayday}</h2>
        <Link to="/m/payments" className={QUIET_LINK}>
          {copy.seePayments}
        </Link>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-muted">{copy.nothingDue}</p>
      ) : (
        <ul aria-label={copy.dueBeforePayday} className="divide-y divide-divider">
          {items.slice(0, max).map((i) => (
            <li key={i.id} className="flex min-h-11 items-center justify-between gap-3">
              <span className="min-w-0 text-fg">
                {i.label}
                <span className="text-sm text-muted">
                  {' '}
                  · {dayShort(i.date)}
                  {i.overdue
                    ? ` · ${copy.tagOverdue}`
                    : i.estimated
                      ? ` · ${copy.tagEstimated}`
                      : ''}
                </span>
              </span>
              <span className="shrink-0 font-mono tabular-nums text-fg">
                {formatPeso(i.amountMinor, { cents: 'auto' })}
              </span>
            </li>
          ))}
          {items.length > max ? (
            <li className="py-2 text-sm text-muted">+{items.length - max} more</li>
          ) : null}
        </ul>
      )}
    </section>
  );
}

/** "Coming in": expected money that isn't income, with a Received action. */
export function ComingInCard({
  items,
  accountName,
  today,
}: {
  items: readonly ComingInItem[];
  accountName(id: string | null): string | null;
  today: string;
}) {
  const total = items.reduce((s, e) => s + e.amountMinor, 0);
  return (
    <section aria-label={copy.comingIn} className={`${CARD} flex flex-col gap-2`}>
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-[20px] font-bold text-fg">{copy.comingIn}</h2>
        <Link to="/m/budget/expected/new" className={QUIET_LINK}>
          {copy.addExpected}
        </Link>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-muted">{copy.nothingComingIn}</p>
      ) : (
        <>
          <ul aria-label={copy.comingIn} className="divide-y divide-divider">
            {items.map((e) => {
              const late = e.date < today;
              const into = accountName(e.accountId);
              const repeat = copy.repeatTag[e.repeat];
              return (
                <li key={e.key} className="flex min-h-11 items-center justify-between gap-3 py-1">
                  <Link to={`/m/budget/expected/${e.id}`} className={`min-w-0 text-fg ${FOCUS}`}>
                    {e.name}
                    <span className="block text-sm text-muted">
                      {dayShort(e.date)}
                      {repeat ? ` · ${repeat}` : ''}
                      {into ? ` · ${into}` : ''}
                      {late ? ` · ${copy.notYetReceived}` : ''}
                    </span>
                  </Link>
                  <span className="flex shrink-0 items-center gap-2">
                    <span className="font-mono tabular-nums text-fg">
                      {formatPeso(e.amountMinor, { cents: 'auto' })}
                    </span>
                    <Link
                      to={`/m/budget/expected/${e.id}/received${e.repeat === 'none' ? '' : `?on=${e.occurrenceDate}`}`}
                      aria-label={`${copy.markReceived}: ${e.name}${e.repeat === 'none' ? '' : `, ${dayShort(e.date)}`}`}
                      className={QUIET_LINK}
                    >
                      {copy.markReceived}
                    </Link>
                  </span>
                </li>
              );
            })}
          </ul>
          <p className="text-sm text-muted">
            {copy.expectedTotal(total)} · {copy.comingInNote}
          </p>
        </>
      )}
    </section>
  );
}
